import React, { useState } from 'react';
import { getEmailValidationError } from '../../utils/validation';

/**
 * Editable envelope for the dashboard Send dialogs (REG-675).
 *
 * Shows exactly who will receive the email and lets the rep change it within
 * the policy the send-rep-email edge function enforces server-side:
 *  - To: one of the addresses on the merchant record (email / alternative);
 *  - CC / BCC: @skylinecaps.com addresses only, admin@ is locked, the sender
 *    always receives a copy.
 * The component is presentation + local validation only; the function
 * re-validates every address.
 */

export const INTERNAL_EMAIL_DOMAIN = '@skylinecaps.com';

export function isInternalEmail(value: string): boolean {
  return value.trim().toLowerCase().endsWith(INTERNAL_EMAIL_DOMAIN);
}

export function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

export interface RecipientListProps {
  label: 'CC' | 'Bcc';
  /** Addresses that cannot be removed (admin@, the sender copy). */
  locked: string[];
  /** Addresses the rep may remove. */
  editable: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /** Extra addresses allowed beyond the locked ones (server cap). */
  max?: number;
}

export function RecipientList({ label, locked, editable, onChange, disabled, max = 5 }: RecipientListProps): React.ReactElement {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = () => {
    const value = normalizeEmail(draft);
    if (!value) return;
    const invalid = getEmailValidationError(value);
    if (invalid) { setError(invalid); return; }
    if (!isInternalEmail(value)) { setError(`Only ${INTERNAL_EMAIL_DOMAIN} addresses can be copied.`); return; }
    if (locked.includes(value) || editable.includes(value)) { setError('Already on the email.'); return; }
    if (editable.length >= max) { setError(`Up to ${max} extra ${label} addresses.`); return; }
    onChange([...editable, value]);
    setDraft('');
    setError(null);
  };

  return (
    <div className="send-offer-recipients">
      <span className="send-offer-envelope-label">{label}</span>
      <div className="send-offer-chips">
        {locked.map((email) => (
          <span key={`locked-${email}`} className="send-offer-chip send-offer-chip-locked" title="Always included">{email}</span>
        ))}
        {editable.map((email) => (
          <span key={email} className="send-offer-chip">
            {email}
            <button
              type="button"
              className="send-offer-chip-remove"
              aria-label={`Remove ${email} from ${label}`}
              disabled={disabled}
              onClick={() => onChange(editable.filter((e) => e !== email))}
            >×</button>
          </span>
        ))}
        <input
          className="send-offer-chip-input"
          type="email"
          placeholder={`Add ${INTERNAL_EMAIL_DOMAIN}…`}
          aria-label={`Add ${label} address`}
          value={draft}
          disabled={disabled}
          onChange={(e) => { setDraft(e.target.value); setError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
          onBlur={add}
        />
      </div>
      {error && <span className="send-offer-chip-error" role="alert">{error}</span>}
    </div>
  );
}

export interface RecipientToProps {
  /** Addresses on the merchant record; the only valid destinations. */
  options: string[];
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}

export function RecipientTo({ options, value, onChange, disabled }: RecipientToProps): React.ReactElement {
  return (
    <div className="send-offer-recipients">
      <span className="send-offer-envelope-label">To</span>
      {options.length > 1 ? (
        <select
          className="send-offer-to-select"
          aria-label="Send to"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        >
          {options.map((email) => <option key={email} value={email}>{email}</option>)}
        </select>
      ) : (
        <span className="send-offer-chip send-offer-chip-locked" title="The merchant's email on file">{value || '—'}</span>
      )}
      <small className="send-offer-recipients-hint">Merchant emails on file only</small>
    </div>
  );
}
